/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import ReactDOMServer from "react-dom/server";

import { API, graphqlOperation } from "aws-amplify";
import { updateInvoice } from "../../../graphql/mutations";

import {
  Backdrop,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  TextField as InputField,
  Switch,
  Typography,
  Tooltip,
} from "@material-ui/core";

import { Button, useListContext, useNotify, useRefresh } from "react-admin";

import { CancelIcon, EmailIcon } from "../../../components/common/iconography";
import { logError, logHistory } from "../../../utils/appUtils";
import {
  ExternalStatus,
  InvoiceStatus,
  REGEX_MULTI_EMAIL,
  ResourceType,
} from "../../../utils/constants";
import { getInvoiceForSend } from "../../../graphql/invoice/invoice-queries";
import {
  fetchTriggerEnabled,
  getTriggerEnabled,
  hasExternalId,
  sendEmail,
  triggerEvent,
} from "../../../utils/zapierUtils";
import {
  generateEmailSubject,
  splitEmails,
  trimEmail,
} from "../../../utils/utils";
import { EmailInvoiceBody } from "./InvoiceShow";
import { setInvoiceSyncFailed } from "./InvoiceUtils";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

// Consolidated helper to prepare invoice email artifacts (html, subject, emailBody)
export const buildInvoiceEmailPayload = (
  invoice,
  {
    to,
    cc,
    encloseAttachments,
    template = "emailTemplateWide.html",
    subjectPrefix,
    subjectOverride,
  } = {}
) => {
  const htmlDoc = ReactDOMServer.renderToString(
    <EmailInvoiceBody
      record={invoice}
      encloseAttachments={encloseAttachments}
    />
  );

  const baseSubject =
    subjectOverride ||
    generateEmailSubject(invoice.account?.invoiceSubject, invoice);
  const subject = subjectPrefix
    ? `${subjectPrefix}${baseSubject || ""}`.trim()
    : baseSubject;

  const emailBody = {
    from: "no_reply transloads.co <invoices@transloads.co>",
    to: splitEmails(to),
    cc: cc ? splitEmails(cc) : undefined,
    subject,
    template,
    htmlContent: htmlDoc,
    attachPDF: true,
  };

  return emailBody;
};

const validateEmail = (email) => {
  if (email) return REGEX_MULTI_EMAIL.test(email.trim());
  return true;
};

// "To email" helper text: a validation error first, then a note when the
// account has no billing email to prefill (the field is optional).
const getToEmailHelperText = (isEmailValid, billingEmail, fallback = false) => {
  if (!isEmailValid) return "Please enter a valid email address.";
  if (!billingEmail) return "No billing email on account";
  return fallback;
};

export const SendInvoiceButton = ({ record }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [sending, setSending] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [isSendEnabled, setIsSendEnabled] = useState(
    getTriggerEnabled("invoiceSent") &&
      hasExternalId(record.account) &&
      record.externalStatus === ExternalStatus.SYNCED
  );

  useEffect(() => {
    let active = true; // prevent memory leaks - setting state after component unmounts

    const checkSyncEnabled = async () => {
      const triggerEnabled = await fetchTriggerEnabled("invoiceSent");
      if (active)
        setIsSendEnabled(
          triggerEnabled &&
            hasExternalId(record.account) &&
            record.externalStatus === ExternalStatus.SYNCED
        );
    };
    checkSyncEnabled();
    return () => (active = false);
  }, [record]);

  const handleOpenDialog = () => {
    if (isSendEnabled) {
      if (!hasExternalId(record.account)) {
        notify("Account nos synced to external system.", "warning");
        return;
      }
    }
    setEmailOpen(true);
  };

  const isButtonDisabled =
    record.externalStatus === ExternalStatus.SYNC_PENDING ||
    record.externalStatus === ExternalStatus.SYNC_FAILED ||
    !record?.lineItems?.items?.length ||
    !record?.account;

  const getTooltipTitle = () => {
    if (record.externalStatus === ExternalStatus.SYNC_PENDING)
      return "Invoice is pending sync. Refresh and try again.";
    if (record.externalStatus === ExternalStatus.SYNC_FAILED)
      return "Invoice sync failed. Resync and try again.";
    if (!record?.lineItems?.items?.length) return "Invoice has no line items";
    return "";
  };

  if (record.status === InvoiceStatus.PAID) return null;
  return (
    <>
      {isButtonDisabled ? (
        <Tooltip title={getTooltipTitle()} arrow placement="left">
          <span>
            <Button label="Send" disabled>
              <EmailIcon />
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Button
          label="Send"
          color="primary"
          onClick={handleOpenDialog}
          disabled={sending}
        >
          {sending ? <CircularProgress size={20} /> : <EmailIcon />}
        </Button>
      )}
      {isSendEnabled && emailOpen && (
        <SendExternalInvoiceDialog
          invoice={record}
          emailOpen={emailOpen}
          setEmailOpen={setEmailOpen}
          sending={sending}
          setSending={setSending}
          notify={notify}
        />
      )}
      {!record.externalStatus && emailOpen && (
        <EmailInvoiceDialog
          invoice={record}
          emailOpen={emailOpen}
          setEmailOpen={setEmailOpen}
          sending={sending}
          setSending={setSending}
          notify={notify}
          refresh={refresh}
        />
      )}
    </>
  );
};

export const BulkSendButton = (props) => {
  const classes = useStyles();
  const notify = useNotify();
  const refresh = useRefresh();
  const {
    // fetched data
    data, // an id-based dictionary of the list data, e.g. { 123: { id: 123, title: 'hello world' }, 456: { ... } }
    selectedIds, // an array listing the ids of the selected rows, e.g. [123, 456]
    onUnselectItems,
  } = useListContext();
  const [sending, setSending] = useState(false);
  const [isSendEnabled, setIsSendEnabled] = useState(
    getTriggerEnabled("invoiceSent")
  );
  const [isEmailEnabled, setIsEmailEnabled] = useState(
    getTriggerEnabled("newEmail")
  );

  useEffect(() => {
    let active = true; // prevent memory leaks - setting state after component unmounts

    const checkSyncEnabled = async () => {
      const sendEnabled = await fetchTriggerEnabled("invoiceSent");
      const emailEnabled = await fetchTriggerEnabled("newEmail");
      if (active) setIsSendEnabled(sendEnabled);
      if (active) setIsEmailEnabled(emailEnabled);
    };
    checkSyncEnabled();
    return () => (active = false);
  }, []);

  const sendBulkInvoices = async () => {
    try {
      setSending(true);

      let promiseList = [];

      // Fetch fresh invoice data for ALL selected IDs
      selectedIds.forEach((invoiceId) => {
        promiseList.push(
          API.graphql(graphqlOperation(getInvoiceForSend, { id: invoiceId }))
        );
      });
      const results = await Promise.all(promiseList);
      results.forEach((res) => {
        data[res.data.getInvoice.id] = res.data.getInvoice;
      });

      // Separate invoices by their external status. Billing email is optional,
      // so invoices whose account has none are skipped and only counted. That
      // includes external sends: accounts sync from the external system, so a
      // missing email here means it is missing there too.
      const externalInvoices = [];
      const emailInvoices = [];
      let skippedCount = 0;

      selectedIds.forEach((invoiceId) => {
        const record = data[invoiceId];
        if (record && record.status !== InvoiceStatus.PAID) {
          if (!trimEmail(record.account?.billingEmail)) {
            skippedCount++;
          } else if (record.externalStatus) {
            // External system send
            externalInvoices.push(record);
          } else {
            // SES or Zapier Email send
            emailInvoices.push(record);
          }
        }
      });

      // validate all external invoices are eligible
      const invalidExternalInvoices = externalInvoices.filter(
        (inv) =>
          inv.externalStatus !== ExternalStatus.SYNCED || // stil being created / synced / failed
          !hasExternalId(inv.account) // account not synced
      );

      if (invalidExternalInvoices.length > 0) {
        notify("Unselect pending invoices and try again.", "warning");
        setSending(false);
        return;
      }

      // Send external invoices (via triggerEvent)
      const externalPromises = externalInvoices.map((invoice) => {
        return triggerEvent(
          {
            externalId: invoice.externalId,
            email: trimEmail(invoice.account.billingEmail),
          },
          "invoiceSent"
        )
          .then(() => {
            const historyNote = {
              text: "Invoice sent via external system.",
              parentId: invoice.id,
              parentType: ResourceType.INVOICE,
            };
            logHistory(historyNote);
          })
          .catch((error) => {
            logError(`invoices:BulkSendButton:external:${invoice.id}`, error);
          });
      });

      // Send regular invoices (via email)
      const emailPromises = emailInvoices.map(async (invoice) => {
        try {
          const billingEmail = trimEmail(invoice.account.billingEmail);
          const emailBody = buildInvoiceEmailPayload(invoice, {
            to: billingEmail,
            cc: invoice?.contact?.email,
            encloseAttachments: invoice.account?.invoiceAttachments,
          });

          await handleEmailSending(invoice, billingEmail, emailBody, notify);
        } catch (error) {
          logError(
            `invoices:BulkSendButton:regular:${invoice.id}`,
            error,
            notify
          );
        }
      });

      await Promise.allSettled([...externalPromises, ...emailPromises]);

      const totalSent = externalInvoices.length + emailInvoices.length;
      if (skippedCount > 0)
        notify(
          `${totalSent} invoice(s) sent, ${skippedCount} skipped (no billing email).`,
          "warning"
        );
      else notify(`${totalSent} invoice(s) sent successfully.`, "info");
      onUnselectItems();
      refresh();
    } catch (error) {
      logError("invoices:BulkSendButton:sendInvoices", error, notify);
    } finally {
      setSending(false);
    }
  };

  const handleBulkSend = (e) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    sendBulkInvoices();
  };

  if (!isSendEnabled && !isEmailEnabled) return null;
  return (
    <>
      <Backdrop className={classes.backdrop} open={sending}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Button
        label="Send"
        onClick={handleBulkSend}
        color={props.color || "default"}
        disabled={sending}
      >
        <EmailIcon />
      </Button>
    </>
  );
};

const SendExternalInvoiceDialog = ({
  invoice,
  emailOpen,
  setEmailOpen,
  sending,
  setSending,
  notify,
}) => {
  const billingEmail = trimEmail(invoice.account?.billingEmail);
  const [email, setEmail] = useState(billingEmail);
  const [isEmailValid, setIsEmailValid] = useState(true);

  const handleEmailChange = (event) => {
    const emailInput = event.target.value;
    setEmail(emailInput);
  };

  const handleEmailBlur = () => {
    setIsEmailValid(validateEmail(email));
  };

  const handleSendExternal = () => {
    const valid = validateEmail(email);
    setIsEmailValid(valid);
    if (!valid) {
      notify("Enter a valid email address.", "warning");
      return;
    }

    try {
      setSending(true);
      triggerEvent(
        { externalId: invoice.externalId, email: email },
        "invoiceSent"
      )
        .then(() => {
          const historyNote = {
            text: "Invoice sent via external system.",
            parentId: invoice.id,
            parentType: ResourceType.INVOICE,
          };
          logHistory(historyNote);
        })
        .catch((error) => {
          setInvoiceSyncFailed(invoice);
          logError("invoices:SendButton:handleSendExternal", error, notify);
        });

      notify("Send invoice submitted.", "info");
      setEmailOpen(false);
    } catch (error) {
      setInvoiceSyncFailed(invoice);
      logError("invoices:SendButton:handleSendExternal", error, notify);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={emailOpen}
      maxWidth="sm"
      onClose={() => setEmailOpen(false)}
      fullWidth
    >
      <DialogTitle>External Send Invoice </DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <InputField
              label="To email"
              size="small"
              variant="outlined"
              fullWidth
              required
              value={email}
              error={!isEmailValid} // Display error state based on validation
              helperText={getToEmailHelperText(
                isEmailValid,
                billingEmail,
                "Comma-separated email(s)"
              )}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          label="CANCEL"
          color="default"
          onClick={() => setEmailOpen(false)}
        >
          <CancelIcon />
        </Button>
        <Button
          label="SEND"
          color="primary"
          disabled={!email.trim() || !isEmailValid || sending}
          onClick={handleSendExternal}
        >
          <EmailIcon />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const handleEmailSending = async (record, toEmail, emailBody, notify) => {
  // Asynchronous function to handle email sending and conditional invoice update
  try {
    sendEmail(
      emailBody,
      "invoices:InvoiceShow:sendInvoice:handleEmailSending",
      notify
    );
    const historyNote = {
      text: `Invoice sent to : ${toEmail}`,
      parentId: record.id,
      parentType: ResourceType.INVOICE,
    };
    logHistory(historyNote);

    // If email sending is successful, update the invoice
    const input = {
      id: record.id,
      active: record.active,
      reloadYardId: record.reloadYardId,
      dueDate: record.dueDate,
      accountId: record.accountId,
      accountName: record.accountName,
      status:
        record.status === InvoiceStatus.NEW
          ? InvoiceStatus.PENDING
          : record.status,
      sentDate: new Date(),
    };
    const updateRes = await API.graphql(
      graphqlOperation(updateInvoice, { input })
    );
    const updateErrors = updateRes?.errors;
    const updated = updateRes?.data?.updateInvoice;

    // Amplify/API.graphql can resolve with { data: null, errors: [...] } without throwing.
    // If we don't explicitly check, we'll incorrectly proceed as if the update succeeded.
    if (updateErrors?.length > 0 || !updated?.id) {
      const err = new Error("updateInvoice returned errors");
      err.errors = updateErrors || [
        { message: "Missing updateInvoice in response" },
      ];
      err.response = updateRes;
      throw err;
    }
  } catch (error) {
    logError("invoices:SendButton:handleEmailSending", error, notify);
  }
};

export const EmailInvoiceDialog = ({
  invoice,
  emailOpen,
  setEmailOpen,
  sending,
  setSending,
  notify,
  refresh,
  dialogTitle = "Send Invoice",
  message,
  subjectPrefix,
  subjectOverride,
  onCancel,
  onSubmit,
  ...props
}) => {
  const billingEmail = trimEmail(invoice.account?.billingEmail);
  const [email, setEmail] = useState(billingEmail);
  const [cc, setCC] = useState(invoice?.contact?.email || "");
  const [encloseAttachments, setEncloseAttachments] = useState(
    invoice.account?.invoiceAttachments
  );
  const [subject, setSubject] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);

  // The subject is required: an empty field reads as red the moment it is
  // cleared, rather than silently falling back to the account's template.
  const subjectMissing = !subject.trim();

  useEffect(() => {
    setEmail(billingEmail);
    setCC(invoice?.contact?.email || "");
    setEncloseAttachments(invoice.account?.invoiceAttachments);

    const baseSubject = generateEmailSubject(
      invoice.account?.invoiceSubject,
      invoice
    );
    const nextSubject = (() => {
      if (subjectOverride) return subjectOverride;
      if (subjectPrefix) return `${subjectPrefix}${baseSubject || ""}`.trim();
      return baseSubject || "";
    })();
    setSubject(nextSubject);
  }, [invoice?.id, subjectOverride, subjectPrefix]);

  const handleEmailChange = (event) => {
    const emailInput = event.target.value;
    setEmail(emailInput);
  };

  const handleEmailBlur = () => {
    setIsEmailValid(validateEmail(email));
  };

  const handleSend = async () => {
    try {
      const account = invoice?.account;
      const valid = validateEmail(email);
      if (valid) setIsEmailValid(valid);
      else {
        notify("Please enter a valid email address.", "warning");
        return;
      }

      if (!email.trim()) {
        notify("Account missing billing email. Invoice not sent.", "warning");
        logError(
          "invoices:InvoiceShow:sendInvoice:account no email",
          JSON.stringify(account),
          false
        );
        return;
      }

      if (subjectMissing) {
        notify("Please enter an email subject.", "warning");
        return;
      }

      setSending(true);
      const emailBody = buildInvoiceEmailPayload(invoice, {
        to: email,
        cc,
        encloseAttachments,
        subjectOverride: subject,
      });
      if (onSubmit) {
        await onSubmit({
          to: email,
          cc,
          subject,
          encloseAttachments,
          emailBody,
        });
      } else {
        // Call the handleEmailSending function without awaiting it
        await handleEmailSending(invoice, email, emailBody, notify);
        if (refresh) refresh();
      }
    } catch (error) {
      logError("invoices:SendInvoiceDialog:handleSend", error, notify);
    } finally {
      setEmailOpen(false);
      setSending(false);
    }
  };

  const handleCancel = async () => {
    try {
      if (onCancel) await onCancel();
    } finally {
      setEmailOpen(false);
    }
  };

  return (
    <Dialog
      open={emailOpen}
      maxWidth="sm"
      onClose={() => setEmailOpen(false)}
      fullWidth
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          {!!message && (
            <Grid item xs={12}>
              <Typography component="div">{message}</Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <InputField
              label="To email"
              size="small"
              variant="outlined"
              fullWidth
              required
              value={email}
              error={!isEmailValid} // Display error state based on validation
              helperText={getToEmailHelperText(isEmailValid, billingEmail)}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
            />
          </Grid>
          <Grid item xs={12}>
            <InputField
              label="CC"
              size="small"
              variant="outlined"
              fullWidth
              helperText="Comma-separated email(s)"
              value={cc}
              onChange={(event) => setCC(event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <InputField
              label="Subject"
              size="small"
              variant="outlined"
              fullWidth
              required
              value={subject}
              error={subjectMissing}
              helperText={subjectMissing ? "Required" : false}
              onChange={(event) => setSubject(event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Switch
              name="enclose-attachments"
              color="primary"
              checked={encloseAttachments}
              onChange={() => setEncloseAttachments(!encloseAttachments)}
            />
            <Typography component="span">Enclose Attachments</Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          label="CANCEL"
          color="default"
          onClick={handleCancel}
        >
          <CancelIcon />
        </Button>
        <Button
          label="SEND"
          color="primary"
          disabled={!email.trim() || !isEmailValid || sending}
          onClick={handleSend}
        >
          <EmailIcon />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
